import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Users, TrendingUp, Activity, Download, RefreshCw, AlertTriangle, CheckCircle, XCircle, MessageCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, startOfDay, endOfDay, startOfWeek } from 'date-fns';

interface BenchmarkUser {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  status: string;
  created_at: string;
  telegram_connected: boolean;
  entries_today: number;
  entries_this_week: number;
  entries_total: number;
  last_entry_date: string | null;
}

interface DailyEntry {
  date: string;
  user_id: string;
  user_name: string;
  farm_prices: number;
  distribution_center: number;
  local_shops: number;
  sunday_market: number;
  supermarket: number;
  ecommerce: number;
  total: number;
}

const BenchmarkPerformanceReport: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState(7); // days
  const [coordDateRange, setCoordDateRange] = useState(7); // days for coordinator report

  // Fetch benchmark users with their statistics
  const { data: benchmarkUsers, isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['benchmark-users-performance'],
    queryFn: async (): Promise<BenchmarkUser[]> => {
      console.log('=== BENCHMARK PERFORMANCE REPORT QUERY START ===');
      
      // Get all benchmark users - using string comparison instead of enum
      console.log('Querying user_profiles for benchmark users...');
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'benchmark')
        .eq('status', 'active');

      if (usersError) {
        console.error('Error fetching benchmark users:', usersError);
        throw usersError;
      }

      console.log('✓ Raw query result:', users);
      console.log('✓ Found benchmark users:', users?.length || 0);

      if (!users || users.length === 0) {
        console.log('No benchmark users found - checking all users to debug...');
        
        // Debug query to see all users and their roles
        const { data: allUsers, error: debugError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, role, status')
          .limit(20);
        
        if (!debugError && allUsers) {
          console.log('DEBUG: All users in system:', allUsers);
          const benchmarkUsersFound = allUsers.filter(u => u.role === 'benchmark');
          console.log('DEBUG: Benchmark users found in all users:', benchmarkUsersFound);
        }
        
        return [];
      }

      // Get today's date range
      const today = new Date();
      const startToday = startOfDay(today);
      const endToday = endOfDay(today);
      const startWeek = startOfDay(subDays(today, 7));

      console.log('Date ranges:', {
        today: startToday.toISOString(),
        endToday: endToday.toISOString(),
        startWeek: startWeek.toISOString()
      });

      // For each user, get their entry statistics
      const usersWithStats = await Promise.all(
        users.map(async (user) => {
          console.log(`\n--- Processing user: ${user.first_name} ${user.last_name} (${user.id}) ---`);
          
          // Count entries from all price tables for today
          const todayPromises = [
            supabase.from('farm_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString()),
            supabase.from('distribution_center_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString()),
            supabase.from('local_shop_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString()),
            supabase.from('sunday_market_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString()),
            supabase.from('supermarket_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString()),
            supabase.from('ecommerce_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startToday.toISOString()).lte('created_at', endToday.toISOString())
          ];

          const [farmToday, dcToday, localToday, sundayToday, superToday, ecommerceToday] = await Promise.all(todayPromises);

          const entriesToday = (farmToday.count || 0) + (dcToday.count || 0) + (localToday.count || 0) + (sundayToday.count || 0) + (superToday.count || 0) + (ecommerceToday.count || 0);
          console.log(`Today's entries: ${entriesToday} (farm:${farmToday.count}, dc:${dcToday.count}, local:${localToday.count}, sunday:${sundayToday.count}, super:${superToday.count}, ecommerce:${ecommerceToday.count})`);

          // Count entries for this week
          const weekPromises = [
            supabase.from('farm_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString()),
            supabase.from('distribution_center_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString()),
            supabase.from('local_shop_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString()),
            supabase.from('sunday_market_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString()),
            supabase.from('supermarket_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString()),
            supabase.from('ecommerce_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', startWeek.toISOString())
          ];

          const [farmWeek, dcWeek, localWeek, sundayWeek, superWeek, ecommerceWeek] = await Promise.all(weekPromises);

          const entriesWeek = (farmWeek.count || 0) + (dcWeek.count || 0) + (localWeek.count || 0) + (sundayWeek.count || 0) + (superWeek.count || 0) + (ecommerceWeek.count || 0);
          console.log(`Week's entries: ${entriesWeek}`);

          // Count total entries
          const totalPromises = [
            supabase.from('farm_prices').select('id', { count: 'exact' }).eq('created_by', user.id),
            supabase.from('distribution_center_prices').select('id', { count: 'exact' }).eq('created_by', user.id),
            supabase.from('local_shop_prices').select('id', { count: 'exact' }).eq('created_by', user.id),
            supabase.from('sunday_market_prices').select('id', { count: 'exact' }).eq('created_by', user.id),
            supabase.from('supermarket_prices').select('id', { count: 'exact' }).eq('created_by', user.id),
            supabase.from('ecommerce_prices').select('id', { count: 'exact' }).eq('created_by', user.id)
          ];

          const [farmTotal, dcTotal, localTotal, sundayTotal, superTotal, ecommerceTotal] = await Promise.all(totalPromises);

          const entriesTotal = (farmTotal.count || 0) + (dcTotal.count || 0) + (localTotal.count || 0) + (sundayTotal.count || 0) + (superTotal.count || 0) + (ecommerceTotal.count || 0);
          console.log(`Total entries: ${entriesTotal} (farm:${farmTotal.count}, dc:${dcTotal.count}, local:${localTotal.count}, sunday:${sundayTotal.count}, super:${superTotal.count}, ecommerce:${ecommerceTotal.count})`);

          // Get last entry date
          const lastEntryPromises = [
            supabase.from('farm_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1),
            supabase.from('distribution_center_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1),
            supabase.from('local_shop_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1),
            supabase.from('sunday_market_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1),
            supabase.from('supermarket_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1),
            supabase.from('ecommerce_prices').select('created_at').eq('created_by', user.id).order('created_at', { ascending: false }).limit(1)
          ];

          const allLastEntries = await Promise.all(lastEntryPromises);

          const lastEntryDates = allLastEntries
            .filter(result => result.data && result.data.length > 0)
            .map(result => new Date(result.data![0].created_at))
            .sort((a, b) => b.getTime() - a.getTime());

          const lastEntryDate = lastEntryDates.length > 0 ? lastEntryDates[0].toISOString() : null;
          console.log(`Last entry date: ${lastEntryDate}`);

          // Check if user has telegram connection
          const { data: telegramConnection } = await supabase
            .from('telegram_benchmark_connections')
            .select('id')
            .eq('user_id', user.id)
            .single();

          console.log(`Telegram connected: ${!!telegramConnection}`);

          const userWithStats = {
            ...user,
            telegram_connected: !!telegramConnection,
            entries_today: entriesToday,
            entries_this_week: entriesWeek,
            entries_total: entriesTotal,
            last_entry_date: lastEntryDate
          };

          console.log(`✓ User ${user.first_name} final stats:`, userWithStats);
          return userWithStats;
        })
      );

      console.log('=== FINAL RESULTS ===');
      console.log(`Total users processed: ${usersWithStats.length}`);
      console.log('Users with entries:', usersWithStats.filter(u => u.entries_total > 0).length);
      console.log('Final user list:', usersWithStats.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, total: u.entries_total })));
      console.log('=== BENCHMARK PERFORMANCE REPORT QUERY END ===');
      
      return usersWithStats;
    },
  });

  const { data: dailyEntries, isLoading: entriesLoading, refetch: refetchEntries } = useQuery({
    queryKey: ['daily-entries', dateRange],
    queryFn: async (): Promise<DailyEntry[]> => {
      console.log('=== DAILY ENTRIES QUERY START ===');
      const endDate = new Date();
      const startDate = subDays(endDate, dateRange);

      // Get all benchmark users
      const { data: users } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .eq('role', 'benchmark')
        .eq('status', 'active');

      if (!users) {
        console.log('No users found for daily entries');
        return [];
      }

      console.log(`Processing daily entries for ${users.length} users over ${dateRange} days`);

      const dailyData: DailyEntry[] = [];

      // For each day in the range
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayStart = startOfDay(new Date(d));
        const dayEnd = endOfDay(new Date(d));
        const dateStr = format(dayStart, 'yyyy-MM-dd');

        // For each user, get their entries for this day
        for (const user of users) {
          const dayPromises = [
            supabase.from('farm_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString()),
            supabase.from('distribution_center_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString()),
            supabase.from('local_shop_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString()),
            supabase.from('sunday_market_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString()),
            supabase.from('supermarket_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString()),
            supabase.from('ecommerce_prices').select('id', { count: 'exact' }).eq('created_by', user.id).gte('created_at', dayStart.toISOString()).lte('created_at', dayEnd.toISOString())
          ];

          const [farm, dc, local, sunday, super_, ecommerce] = await Promise.all(dayPromises);

          const farmCount = farm.count || 0;
          const dcCount = dc.count || 0;
          const localCount = local.count || 0;
          const sundayCount = sunday.count || 0;
          const superCount = super_.count || 0;
          const ecommerceCount = ecommerce.count || 0;
          const total = farmCount + dcCount + localCount + sundayCount + superCount + ecommerceCount;

          // Include entries even if total is 0 for recent dates (last 3 days) to show inactive users
          const includeEntry = total > 0 || (new Date().getTime() - dayStart.getTime()) <= (3 * 24 * 60 * 60 * 1000);
          
          if (includeEntry) {
            dailyData.push({
              date: dateStr,
              user_id: user.id,
              user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown',
              farm_prices: farmCount,
              distribution_center: dcCount,
              local_shops: localCount,
              sunday_market: sundayCount,
              supermarket: superCount,
              ecommerce: ecommerceCount,
              total
            });
          }
        }
      }

      console.log(`Daily entries processed: ${dailyData.length} records`);
      console.log('=== DAILY ENTRIES QUERY END ===');
      
      return dailyData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    },
  });

  const { data: coordinatorData, isLoading: coordinatorLoading, refetch: refetchCoordinator } = useQuery({
    queryKey: ['coordinator-entries', coordDateRange],
    queryFn: async () => {
      const endDate = new Date();
      const startDate = subDays(endDate, coordDateRange);

      const { data: coordinators } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, phone_number, status')
        .eq('role', 'coordinator')
        .eq('status', 'active');

      if (!coordinators || coordinators.length === 0) {
        return { daily: [], weekly: [], submissions: [] };
      }

      const coordinatorIds = coordinators.map((c: any) => c.id);

      const { data: submissions, error } = await supabase
        .from('farm_prices')
        .select('id, created_by, created_at, date, product_name, price, region')
        .gte('created_at', startOfDay(startDate).toISOString())
        .lte('created_at', endOfDay(endDate).toISOString())
        .in('created_by', coordinatorIds);

      if (error) throw error;

      const nameFor = (id: string) => {
        const c = coordinators.find((x: any) => x.id === id);
        return `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Unknown';
      };

      const dailyMap = new Map<string, number>();
      (submissions || []).forEach((s: any) => {
        const day = format(new Date(s.created_at), 'yyyy-MM-dd');
        const key = `${day}|${s.created_by}`;
        dailyMap.set(key, (dailyMap.get(key) || 0) + 1);
      });
      const daily = Array.from(dailyMap.entries()).map(([key, total]) => {
        const [date, coordinator_id] = key.split('|');
        return { date, coordinator_id, coordinator_name: nameFor(coordinator_id), total };
      }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const weeklyMap = new Map<string, number>();
      (submissions || []).forEach((s: any) => {
        const ws = format(startOfWeek(new Date(s.created_at), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const key = `${ws}|${s.created_by}`;
        weeklyMap.set(key, (weeklyMap.get(key) || 0) + 1);
      });
      const weekly = Array.from(weeklyMap.entries()).map(([key, total]) => {
        const [week_start, coordinator_id] = key.split('|');
        return { week_start, coordinator_id, coordinator_name: nameFor(coordinator_id), total };
      }).sort((a, b) => new Date(a.week_start).getTime() - new Date(b.week_start).getTime());

      const enrichedSubmissions = (submissions || []).map((s: any) => ({
        ...s,
        coordinator_name: nameFor(s.created_by)
      }));

      return { daily, weekly, submissions: enrichedSubmissions };
    },
  });

  const handleRefresh = () => {
    refetchUsers();
    refetchEntries();
    refetchCoordinator();
  };
  const getStatusBadge = (user: BenchmarkUser) => {
    const daysSinceCreated = Math.floor((new Date().getTime() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24));
    const daysSinceLastEntry = user.last_entry_date 
      ? Math.floor((new Date().getTime() - new Date(user.last_entry_date).getTime()) / (1000 * 60 * 60 * 24))
      : null;

    if (!user.telegram_connected) {
      return (
        <div className="flex items-center gap-1">
          <XCircle className="h-3 w-3 text-red-500" />
          <Badge variant="destructive">No Telegram</Badge>
        </div>
      );
    }
    
    if (user.entries_today > 0) {
      return (
        <div className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3 text-green-500" />
          <Badge variant="default" className="bg-green-500">Active Today</Badge>
        </div>
      );
    }
    
    if (user.entries_this_week > 0) {
      return (
        <div className="flex items-center gap-1">
          <Activity className="h-3 w-3 text-blue-500" />
          <Badge variant="secondary">Active This Week</Badge>
        </div>
      );
    }

    if (user.entries_total === 0 && daysSinceCreated <= 7) {
      return (
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-yellow-500" />
          <Badge variant="outline" className="border-yellow-500 text-yellow-700">New User</Badge>
        </div>
      );
    }
    
    if (daysSinceLastEntry === null || daysSinceLastEntry > 30) {
      return (
        <div className="flex items-center gap-1">
          <XCircle className="h-3 w-3 text-red-500" />
          <Badge variant="destructive">Inactive 30+ days</Badge>
        </div>
      );
    }
    
    if (daysSinceLastEntry > 7) {
      return (
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-yellow-500" />
          <Badge variant="outline" className="border-orange-500 text-orange-700">Inactive 7+ days</Badge>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-1">
        <Activity className="h-3 w-3 text-blue-500" />
        <Badge variant="secondary">Recent Activity</Badge>
      </div>
    );
  };

  // Calculate summary stats
  const totalBenchmarkUsers = benchmarkUsers?.length || 0;
  const activeToday = benchmarkUsers?.filter(u => u.entries_today > 0).length || 0;
  const activeThisWeek = benchmarkUsers?.filter(u => u.entries_this_week > 0).length || 0;
  const telegramConnected = benchmarkUsers?.filter(u => u.telegram_connected).length || 0;
  const neverEntered = benchmarkUsers?.filter(u => u.entries_total === 0).length || 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Benchmark Performance Report</h2>
          <p className="text-muted-foreground">Monitor benchmark users and their data entry performance</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{totalBenchmarkUsers}</div>
            <p className="text-xs text-muted-foreground">Total Benchmark Users</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{activeToday}</div>
            <p className="text-xs text-muted-foreground">Active Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{activeThisWeek}</div>
            <p className="text-xs text-muted-foreground">Active This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-purple-600">{telegramConnected}</div>
            <p className="text-xs text-muted-foreground">Telegram Connected</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{neverEntered}</div>
            <p className="text-xs text-muted-foreground">Never Entered Data</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Benchmark Users ({totalBenchmarkUsers})
          </TabsTrigger>
          <TabsTrigger value="daily" className="gap-2">
            <Activity className="h-4 w-4" />
            Daily Entries
          </TabsTrigger>
          <TabsTrigger value="coordinators" className="gap-2">
            <Activity className="h-4 w-4" />
            Coordinator Data Entries
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                All Benchmark Users Performance
              </CardTitle>
              <CardDescription>
                Complete overview of all benchmark users, their activity status, and data entry statistics
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-12 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : benchmarkUsers && benchmarkUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Today</TableHead>
                      <TableHead>This Week</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Last Entry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {benchmarkUsers.map((user) => {
                      const daysSinceJoined = Math.floor((new Date().getTime() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24));
                      
                      return (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            <div>
                              <div>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">ID: {user.id.slice(0, 8)}...</div>
                            </div>
                          </TableCell>
                          <TableCell>{user.phone_number || 'Not set'}</TableCell>
                          <TableCell>{getStatusBadge(user)}</TableCell>
                          <TableCell className="text-sm">
                            <div>{format(new Date(user.created_at), 'MMM dd, yyyy')}</div>
                            <div className="text-xs text-muted-foreground">{daysSinceJoined} days ago</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.entries_today > 0 ? "default" : "outline"} className={user.entries_today > 0 ? "bg-green-500" : ""}>
                              {user.entries_today}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.entries_this_week > 0 ? "default" : "outline"} className={user.entries_this_week > 0 ? "bg-blue-500" : ""}>
                              {user.entries_this_week}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.entries_total > 0 ? "default" : "outline"}>
                              {user.entries_total}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {user.last_entry_date 
                              ? (
                                <div>
                                  <div>{format(new Date(user.last_entry_date), 'MMM dd, yyyy')}</div>
                                  <div className="text-xs">
                                    {Math.floor((new Date().getTime() - new Date(user.last_entry_date).getTime()) / (1000 * 60 * 60 * 24))} days ago
                                  </div>
                                </div>
                              )
                              : 'Never'
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Benchmark Users Found</p>
                  <p className="text-sm">No users with 'benchmark' role and 'active' status were found in the system.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button
                variant={dateRange === 7 ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(7)}
              >
                Last 7 Days
              </Button>
              <Button
                variant={dateRange === 14 ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(14)}
              >
                Last 14 Days
              </Button>
              <Button
                variant={dateRange === 30 ? "default" : "outline"}
                size="sm"
                onClick={() => setDateRange(30)}
              >
                Last 30 Days
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Daily Data Entries
              </CardTitle>
              <CardDescription>
                Breakdown of data entries by user and source type over the last {dateRange} days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entriesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : dailyEntries && dailyEntries.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Farm</TableHead>
                      <TableHead>DC</TableHead>
                      <TableHead>Local</TableHead>
                      <TableHead>Sunday</TableHead>
                      <TableHead>Super</TableHead>
                      <TableHead>Ecommerce</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyEntries.map((entry, index) => (
                      <TableRow key={`${entry.date}-${entry.user_id}-${index}`} className={entry.total === 0 ? 'opacity-50' : ''}>
                        <TableCell className="font-medium">
                          {format(new Date(entry.date), 'MMM dd')}
                        </TableCell>
                        <TableCell>{entry.user_name}</TableCell>
                        <TableCell>
                          {entry.farm_prices > 0 ? <Badge variant="outline">{entry.farm_prices}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {entry.distribution_center > 0 ? <Badge variant="outline">{entry.distribution_center}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {entry.local_shops > 0 ? <Badge variant="outline">{entry.local_shops}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {entry.sunday_market > 0 ? <Badge variant="outline">{entry.sunday_market}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {entry.supermarket > 0 ? <Badge variant="outline">{entry.supermarket}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {entry.ecommerce > 0 ? <Badge variant="outline">{entry.ecommerce}</Badge> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.total > 0 ? "default" : "outline"}>
                            {entry.total}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No data entries found for the selected period</p>
                  <p className="text-sm mt-2">Users may not have entered any price data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="coordinators" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button
                variant={coordDateRange === 7 ? "default" : "outline"}
                size="sm"
                onClick={() => setCoordDateRange(7)}
              >
                Last 7 Days
              </Button>
              <Button
                variant={coordDateRange === 14 ? "default" : "outline"}
                size="sm"
                onClick={() => setCoordDateRange(14)}
              >
                Last 14 Days
              </Button>
              <Button
                variant={coordDateRange === 30 ? "default" : "outline"}
                size="sm"
                onClick={() => setCoordDateRange(30)}
              >
                Last 30 Days
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const rows = coordinatorData?.weekly || [];
                  const headers = ['week_start', 'coordinator_name', 'total'];
                  const csv = [headers.join(','), ...rows.map((r: any) => [r.week_start, r.coordinator_name, r.total].join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `coordinator_weekly_${coordDateRange}d.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="gap-2"
              >
                <Download className="h-4 w-4" /> Export Weekly CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const rows = coordinatorData?.daily || [];
                  const headers = ['date', 'coordinator_name', 'total'];
                  const csv = [headers.join(','), ...rows.map((r: any) => [r.date, r.coordinator_name, r.total].join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `coordinator_daily_${coordDateRange}d.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="gap-2"
              >
                <Download className="h-4 w-4" /> Export Daily CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const rows = coordinatorData?.submissions || [];
                  const headers = ['created_at', 'coordinator_name', 'product_name', 'region', 'price'];
                  const csv = [headers.join(','), ...rows.map((r: any) => [r.created_at, r.coordinator_name, r.product_name, r.region, r.price].join(','))].join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `coordinator_submissions_${coordDateRange}d.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="gap-2"
              >
                <Download className="h-4 w-4" /> Export Submissions CSV
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Coordinator Data Entries
              </CardTitle>
              <CardDescription>
                Farm price submissions by coordinators only (created_by = coordinator)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {coordinatorLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-muted rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (coordinatorData?.weekly?.length || 0) > 0 ? (
                <div className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold">{new Set((coordinatorData?.submissions || []).map((s: any) => s.created_by)).size}</div>
                        <p className="text-xs text-muted-foreground">Active Coordinators</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold">{coordinatorData?.submissions?.length || 0}</div>
                        <p className="text-xs text-muted-foreground">Total Submissions</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-2xl font-bold">{coordinatorData?.weekly?.length || 0}</div>
                        <p className="text-xs text-muted-foreground">Weekly Records</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Weekly Overview</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Week Start</TableHead>
                          <TableHead>Coordinator</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(coordinatorData?.weekly || []).map((row: any, idx: number) => (
                          <TableRow key={`${row.week_start}-${row.coordinator_id}-${idx}`}>
                            <TableCell className="font-medium">{format(new Date(row.week_start), 'MMM dd, yyyy')}</TableCell>
                            <TableCell>{row.coordinator_name}</TableCell>
                            <TableCell>
                              <Badge variant={row.total > 0 ? 'default' : 'outline'}>{row.total}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Daily Breakdown</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Coordinator</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(coordinatorData?.daily || []).map((row: any, idx: number) => (
                          <TableRow key={`${row.date}-${row.coordinator_id}-${idx}`}>
                            <TableCell className="font-medium">{format(new Date(row.date), 'MMM dd')}</TableCell>
                            <TableCell>{row.coordinator_name}</TableCell>
                            <TableCell>
                              <Badge variant={row.total > 0 ? 'default' : 'outline'}>{row.total}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No coordinator submissions found for the selected period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BenchmarkPerformanceReport;
